import React from 'react';
import { AlertTriangle, RefreshCw, Bug, Check } from 'lucide-react';
import logger from '../../utils/logger';
import { addDoc, collection } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, showDetails: false, reporting: false, reported: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        logger.error('React Error Boundary caught an error', error, { componentStack: errorInfo.componentStack });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false, reporting: false, reported: false });
        window.location.reload();
    };

    handleReport = async () => {
        if (this.state.reported || this.state.reporting) return;
        this.setState({ reporting: true });
        try {
            await addDoc(collection(db, 'error_reports'), {
                userId: auth.currentUser?.uid || 'anonymous',
                error: {
                    message: this.state.error?.message || 'Unknown Error',
                    stack: this.state.error?.stack || 'No stack trace',
                    componentStack: this.state.errorInfo?.componentStack || 'No component stack'
                },
                context: {
                    url: window.location.pathname,
                    userAgent: navigator.userAgent
                },
                status: 'new',
                createdAt: new Date().toISOString()
            });
            this.setState({ reported: true, reporting: false });
        } catch (e) {
            console.error("Failed to report error", e);
            this.setState({ reporting: false });
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black animate-in fade-in duration-300">
                    <div className="w-full max-w-md bg-neutral-900 border border-neutral-800 rounded-3xl p-8 text-center shadow-2xl shadow-red-900/20">
                        <div className="w-20 h-20 bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500 border-2 border-red-900/30">
                            <AlertTriangle size={40} />
                        </div>

                        <h2 className="text-2xl font-black text-white uppercase italic tracking-tight mb-2">
                            Fallo Muscular
                        </h2>

                        <p className="text-neutral-400 text-sm font-medium leading-relaxed mb-8">
                            Parece que hemos intentado levantar más peso del que podíamos. <br />
                            La aplicación ha tenido un problema inesperado.
                        </p>

                        <div className="space-y-3">
                            <button
                                onClick={this.handleReport}
                                disabled={this.state.reporting || this.state.reported}
                                className={`w-full flex items-center justify-center gap-2 font-bold uppercase tracking-wider py-4 rounded-xl transition-all ${this.state.reported
                                        ? 'bg-green-600 text-white shadow-lg shadow-green-900/30'
                                        : 'bg-neutral-800 hover:bg-neutral-700 text-neutral-300'
                                    }`}
                            >
                                {this.state.reported ? <Check size={20} /> : <Bug size={20} />}
                                {this.state.reported ? 'Informe Enviado' : 'Reportar Fallo'}
                            </button>

                            <button
                                onClick={this.handleReset}
                                className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-600 text-white px-6 py-4 rounded-xl font-black uppercase tracking-wider transition-all shadow-lg shadow-red-900/30 hover:shadow-red-900/50 hover:scale-[1.02]"
                            >
                                <RefreshCw size={20} />
                                Reintentar Serie (Recargar)
                            </button>

                            {process.env.NODE_ENV === 'development' && (
                                <button
                                    onClick={() => this.setState(prev => ({ showDetails: !prev.showDetails }))}
                                    className="text-xs font-bold text-neutral-600 hover:text-neutral-400 uppercase tracking-widest transition-colors py-2"
                                >
                                    {this.state.showDetails ? 'Ocultar Detalles' : 'Ver Detalles Técnicos'}
                                </button>
                            )}
                        </div>

                        {this.state.showDetails && this.state.error && (
                            <div className="mt-6 text-left animate-in slide-in-from-top-2 fade-in duration-200">
                                <div className="bg-black rounded-lg border border-neutral-800 p-4 max-h-60 overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-800">
                                    <p className="text-red-400 font-mono text-xs mb-2 break-all">{this.state.error.toString()}</p>
                                    <pre className="text-neutral-500 font-mono text-[10px] whitespace-pre-wrap">
                                        {this.state.errorInfo?.componentStack}
                                    </pre>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
